import { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Spinner,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { DownloadIcon, ViewIcon } from '@chakra-ui/icons';
import { Document, Page, pdfjs } from 'react-pdf';

import { getDashReportPdfUrl, getMvrReportPdfUrl } from 'api/quote-marketplace';
import Storage from 'storage';
import type { MvrReportData, DashReportRef } from 'api/quote-marketplace';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface ReportsViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: number;
  mvrReports: MvrReportData[];
  dashReports: DashReportRef[];
}

interface PdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  title: string;
}

const PdfPreviewModal = ({
  isOpen,
  onClose,
  pdfUrl,
  title,
}: PdfPreviewModalProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);

  const pdfOptions = useMemo(
    () => ({
      httpHeaders: {
        User: Storage.get('user')?.token || '',
      },
    }),
    [],
  );

  const handleDownload = () => {
    window.open(pdfUrl, '_blank');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box
            height="70vh"
            overflowY="auto"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Document
              file={pdfUrl}
              options={pdfOptions}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="70vh"
                >
                  <Spinner size="xl" />
                </Box>
              }
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  width={950}
                />
              ))}
            </Document>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button leftIcon={<DownloadIcon />} mr={3} onClick={handleDownload}>
            Download
          </Button>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ReportsViewerModal = ({
  isOpen,
  onClose,
  quoteId,
  mvrReports,
  dashReports,
}: ReportsViewerModalProps) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const {
    isOpen: isPdfPreviewOpen,
    onOpen: openPdfPreview,
    onClose: closePdfPreview,
  } = useDisclosure();
  const [selectedDashReport, setSelectedDashReport] =
    useState<DashReportRef | null>(null);
  const [selectedMvrReport, setSelectedMvrReport] =
    useState<MvrReportData | null>(null);

  const availableDashReports = dashReports.filter(
    (r) => r.status === 'available' && r.dash_log_id,
  );

  const handlePreviewDash = (report: DashReportRef) => {
    setSelectedMvrReport(null);
    setSelectedDashReport(report);
    openPdfPreview();
  };

  const handleDownloadDash = (report: DashReportRef) => {
    if (!report.dash_log_id) return;
    const endpoint = Storage.get('API_ENDPOINT') || '';
    const token = Storage.get('user')?.token || '';
    const url = `${endpoint}${getDashReportPdfUrl(quoteId, report.dash_log_id)}?user=${token}`;
    window.open(url, '_blank');
  };

  const getDashPdfUrl = () => {
    if (!selectedDashReport?.dash_log_id) return '';
    const endpoint = Storage.get('API_ENDPOINT') || '';
    const token = Storage.get('user')?.token || '';
    return `${endpoint}${getDashReportPdfUrl(quoteId, selectedDashReport.dash_log_id)}?user=${token}`;
  };

  const handlePreviewMvr = (report: MvrReportData) => {
    setSelectedDashReport(null);
    setSelectedMvrReport(report);
    openPdfPreview();
  };

  const handleDownloadMvr = (report: MvrReportData) => {
    const endpoint = Storage.get('API_ENDPOINT') || '';
    const token = Storage.get('user')?.token || '';
    const url = `${endpoint}${getMvrReportPdfUrl(quoteId, report.mvr_request_id)}?token=${token}`;
    window.open(url, '_blank');
  };

  const getMvrPdfUrl = () => {
    if (!selectedMvrReport) return '';
    const endpoint = Storage.get('API_ENDPOINT') || '';
    const token = Storage.get('user')?.token || '';
    return `${endpoint}${getMvrReportPdfUrl(quoteId, selectedMvrReport.mvr_request_id)}?token=${token}`;
  };

  const getDriverLabel = (report: {
    driver_name: string;
    license_number: string;
  }) => {
    if (report.driver_name) return report.driver_name;
    return report.license_number;
  };

  const hasReports = mvrReports.length > 0 || availableDashReports.length > 0;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Validation Reports</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!hasReports ? (
              <Text color="gray.500" py={4}>
                No reports available
              </Text>
            ) : (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="600" mb={2}>
                    DASH Reports
                  </Text>
                  {availableDashReports.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No DASH reports available
                    </Text>
                  ) : (
                    <VStack
                      align="stretch"
                      spacing={2}
                      p={3}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                    >
                      {availableDashReports.map((report, idx) => (
                        <HStack key={idx} justify="space-between">
                          <HStack spacing={2}>
                            <Text fontSize="sm">{getDriverLabel(report)}</Text>
                            <Text fontSize="xs" color="gray.500">
                              (
                              {report.is_full_history
                                ? 'Full History'
                                : 'Carrier'}
                              )
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              leftIcon={<ViewIcon />}
                              variant="outline"
                              onClick={() => handlePreviewDash(report)}
                            >
                              Preview
                            </Button>
                            <Button
                              size="xs"
                              leftIcon={<DownloadIcon />}
                              variant="outline"
                              onClick={() => handleDownloadDash(report)}
                            >
                              Download
                            </Button>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="600" mb={2}>
                    MVR Reports
                  </Text>
                  {mvrReports.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No MVR reports available
                    </Text>
                  ) : (
                    <VStack
                      align="stretch"
                      spacing={2}
                      p={3}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                    >
                      {mvrReports.map((report, idx) => (
                        <HStack key={idx} justify="space-between">
                          <Text fontSize="sm">{getDriverLabel(report)}</Text>
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              leftIcon={<ViewIcon />}
                              variant="outline"
                              isDisabled={!report.pdf_available}
                              onClick={() => handlePreviewMvr(report)}
                            >
                              Preview
                            </Button>
                            <Button
                              size="xs"
                              leftIcon={<DownloadIcon />}
                              variant="outline"
                              isDisabled={!report.pdf_available}
                              onClick={() => handleDownloadMvr(report)}
                            >
                              Download
                            </Button>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {selectedDashReport && (
        <PdfPreviewModal
          isOpen={isPdfPreviewOpen}
          onClose={closePdfPreview}
          pdfUrl={getDashPdfUrl()}
          title={`DASH Report - ${getDriverLabel(selectedDashReport)}`}
        />
      )}

      {selectedMvrReport && (
        <PdfPreviewModal
          isOpen={isPdfPreviewOpen}
          onClose={closePdfPreview}
          pdfUrl={getMvrPdfUrl()}
          title={`MVR Report - ${getDriverLabel(selectedMvrReport)}`}
        />
      )}
    </>
  );
};

export default ReportsViewerModal;
